import {produce} from 'immer';
import React from 'react';
import {ContentTitle} from 'src/design/atoms/Typography';
import {OptionListEditor} from 'src/design/molecules/OptionListEditor';
import type {OptionListEditorItem} from 'src/design/molecules/OptionListEditor';
import type {IFormFieldDropDownOptions} from 'src/types/Form';
import {OPTION_COLOR_SELECT_OPTIONS} from 'src/app/forms/optionColors';
import {v4 as uuidv4} from 'uuid';
import {
  useGetSelectedField,
  useUpdateSelectedField,
} from '../../../hooks/useFormBuilder';

export const DropdownSettings = React.memo(() => {
  const field = useGetSelectedField();
  const updateSelectedField = useUpdateSelectedField();

  const handleSetOption = React.useCallback(
    (assets: IFormFieldDropDownOptions[]) => {
      if (!field) return;

      updateSelectedField(
        produce(field, draft => {
          draft.builderOptions = {
            ...draft.builderOptions,
            selectOptions: assets,
          };
        })
      );
    },
    [field, updateSelectedField]
  );

  const handleSelectOptionChange = React.useCallback(
    (id: string, value: string) => {
      if (!field) return;
      const options: IFormFieldDropDownOptions[] =
        field.builderOptions?.selectOptions ?? [];

      const updatedOptions = options.map(item =>
        item.id === id ? {...item, label: value} : item
      );

      handleSetOption(updatedOptions);
    },
    [field, handleSetOption]
  );

  const handleAddOption = React.useCallback(() => {
    if (!field) return;
    const options: IFormFieldDropDownOptions[] =
      field.builderOptions?.selectOptions ?? [];

    const result = [
      ...options,
      {
        id: uuidv4(),
        label: '',
        orderId: options.length,
      } as IFormFieldDropDownOptions,
    ];

    handleSetOption(result);
  }, [field, handleSetOption]);

  const handleDeleteOption = React.useCallback(
    (id: string) => {
      if (!field) return;
      const options: IFormFieldDropDownOptions[] =
        field.builderOptions?.selectOptions ?? [];

      const filtered = options
        .filter(item => item.id !== id)
        .map((item, index) => ({...item, orderId: index}));

      handleSetOption(filtered);
    },
    [field, handleSetOption]
  );

  const handleColorChange = React.useCallback(
    (id: string, color: string | null) => {
      if (!field) return;
      const options: IFormFieldDropDownOptions[] =
        field.builderOptions?.selectOptions ?? [];

      const updatedOptions = options.map(item =>
        item.id === id ? {...item, color: color ?? undefined} : item
      );

      handleSetOption(updatedOptions);
    },
    [field, handleSetOption]
  );

  const handleReorderOption = React.useCallback(
    (sourceIndex: number, destinationIndex: number) => {
      if (!field) return;
      const options: IFormFieldDropDownOptions[] = [
        ...(field.builderOptions?.selectOptions ?? []),
      ];

      const [moved] = options.splice(sourceIndex, 1);
      options.splice(destinationIndex, 0, moved);

      handleSetOption(options.map((item, index) => ({...item, orderId: index})));
    },
    [field, handleSetOption]
  );

  const items: OptionListEditorItem[] = (
    field?.builderOptions?.selectOptions ?? []
  ).map(item => ({id: item.id, value: item.label, color: item.color}));

  if (!field || (field.type !== 'select' && field.type !== 'select-multiple')) {
    return null;
  }

  return (
    <>
      <ContentTitle text="Dropdown Options" />

      <OptionListEditor
        items={items}
        onAdd={handleAddOption}
        onChange={handleSelectOptionChange}
        onRemove={handleDeleteOption}
        onReorder={handleReorderOption}
        addLabel="+ Add Option"
        emptyMessage="At least one option is required for dropdown fields."
        colorOptions={OPTION_COLOR_SELECT_OPTIONS}
        onColorChange={handleColorChange}
      />
    </>
  );
});
